import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { Chart, ChartData, ChartOptions } from "chart.js";
import { RiseAgentChartData, RiseAgentChartDataset } from "../rise-agent.types";

export type { RiseAgentChartData, RiseAgentChartDataset };

// ─── Colour palettes ──────────────────────────────────────────────────────────

const BRAND_BLUE = "#1987CB";

const SEVERITY_COLORS: Record<number, string[]> = {
    3: ["#6db310", "#f2b92b", "#e90019"],
    4: ["#1987CB", "#6db310", "#f2b92b", "#e90019"],
    5: ["#1987CB", "#6db310", "#f2b92b", "#e90019", "#af19b2"],
};

const CATEGORY_COLORS = [
    "#1987CB",
    "#2ECC71",
    "#E67E22",
    "#9B59B6",
    "#E74C3C",
    "#1ABC9C",
    "#F39C12",
    "#3498DB",
];

function severityPalette(count: number): string[] {
    if (count <= 3) return SEVERITY_COLORS[3].slice(0, count);
    if (count === 4) return SEVERITY_COLORS[4];
    if (count >= 5) return SEVERITY_COLORS[5];
    return SEVERITY_COLORS[3];
}

function sequentialPalette(count: number): string[] {
    if (count <= 1) return [BRAND_BLUE];
    const start = [209, 231, 245];
    const end = [25, 135, 203];
    return Array.from({ length: count }, (_, i) => {
        const t = count === 1 ? 1 : i / (count - 1);
        const r = Math.round(start[0] + (end[0] - start[0]) * t);
        const g = Math.round(start[1] + (end[1] - start[1]) * t);
        const b = Math.round(start[2] + (end[2] - start[2]) * t);
        return `rgb(${r},${g},${b})`;
    });
}

// Used for bar/doughnut
function resolveColors(
    keyword: string | undefined,
    count: number,
): string | string[] {
    switch (keyword) {
        case "severity":
            return severityPalette(count);
        case "category":
            return CATEGORY_COLORS.slice(0, count);
        case "sequential":
            return sequentialPalette(count);
        case "single":
        default:
            return BRAND_BLUE;
    }
}

// Used for line charts — always returns a single saturated color per dataset.
// sequential/single both use BRAND_BLUE because the sequential ramp starts
// near-white and is unreadable as a line stroke.
function resolveLineColor(keyword: string | undefined, index: number): string {
    switch (keyword) {
        case "severity":
            return severityPalette(3)[index % 3];
        case "category":
            return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
        case "sequential":
        case "single":
        default:
            return BRAND_BLUE;
    }
}

// ─── Component ────────────────────────────────────────────────────────────────

const CHART_WIDTH = 440;
const CHART_HEIGHT = 300;

@Component({
    selector: "app-rise-agent-chart",
    templateUrl: "./rise-agent-chart.component.html",
    styleUrls: ["./rise-agent-chart.component.scss"],
})
export class RiseAgentChartComponent
    implements AfterViewInit, OnChanges, OnDestroy
{
    @Input() chartData: RiseAgentChartData | undefined;
    @ViewChild("chartCanvas", { static: true })
    chartCanvas: ElementRef<HTMLCanvasElement>;

    private chart: Chart | null = null;

    ngAfterViewInit(): void {
        if (this.chartData) {
            this.initChart();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes["chartData"] &&
            !changes["chartData"].firstChange &&
            this.chartData
        ) {
            if (this.chart) {
                this.chart.destroy();
                this.chart = null;
            }
            this.initChart();
        }
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
        this.chart = null;
    }

    getChartImage(): string {
        return this.chart ? this.chart.toBase64Image() : "";
    }

    private initChart(): void {
        const canvas = this.chartCanvas?.nativeElement;
        if (!canvas || !this.chartData) return;

        const container = canvas.parentElement;
        const tryCreate = (attempts: number) => {
            if (attempts <= 0) {
                console.warn(
                    "[RISE CHART] Canvas container never got dimensions — using fallback width",
                );
                this.setCanvasSize(canvas, CHART_WIDTH);
                this.createChart(canvas);
                return;
            }
            const width = container?.offsetWidth ?? 0;
            if (width > 0) {
                this.setCanvasSize(canvas, width);
                this.createChart(canvas);
            } else {
                setTimeout(() => tryCreate(attempts - 1), 100);
            }
        };
        setTimeout(() => tryCreate(20), 50);
    }

    private setCanvasSize(canvas: HTMLCanvasElement, width: number): void {
        // Set both the HTML attributes (rendering resolution) and CSS style
        // (display size). With responsive:false, Chart.js uses the attributes directly.
        canvas.width = width;
        canvas.height = CHART_HEIGHT;
        canvas.style.display = "block";
        canvas.style.width = width + "px";
        canvas.style.height = CHART_HEIGHT + "px";
    }

    private createChart(canvas: HTMLCanvasElement): void {
        if (!this.chartData) return;

        const cd = this.chartData;
        const isDoughnut = cd.type === "doughnut";
        const isLine = cd.type === "line";

        const data: ChartData = {
            labels: cd.labels,
            datasets: cd.datasets.map((ds, i) => {
                const base: Record<string, unknown> = {
                    label: ds.label,
                    data: ds.data,
                };
                if (isLine) {
                    const lineColor = resolveLineColor(ds.backgroundColor, i);
                    base["backgroundColor"] = "transparent";
                    base["borderColor"] = lineColor;
                    base["pointBackgroundColor"] = lineColor;
                    base["borderWidth"] = 2;
                    base["fill"] = false;
                    base["lineTension"] = 0.3;
                    base["pointRadius"] = 4;
                } else {
                    base["backgroundColor"] = resolveColors(
                        ds.backgroundColor,
                        cd.labels.length,
                    );
                }
                return base as any;
            }),
        };

        const options: ChartOptions = {
            responsive: false, // use exact canvas.width / canvas.height
            maintainAspectRatio: false,
            animation: { duration: 400 } as any,
            legend: {
                display: cd.datasets.length > 1,
                position: "top",
                labels: { fontColor: "#333333" } as any,
            },
            title: {
                display: true,
                text: cd.title,
                fontSize: 14,
                fontColor: "#333333",
            },
            tooltips: { enabled: true },
            ...(isDoughnut
                ? { cutoutPercentage: 60 }
                : {
                      scales: {
                          yAxes: [
                              {
                                  ticks: {
                                      beginAtZero: true,
                                      fontColor: "#666666",
                                  },
                                  gridLines: { color: "#E5E5E5" },
                              },
                          ],
                          xAxes: [
                              {
                                  ticks: { fontColor: "#666666" },
                                  gridLines: { color: "#E5E5E5" },
                              },
                          ],
                      },
                  }),
        } as ChartOptions;

        const whiteBackground = {
            beforeDraw: (chart: any) => {
                const ctx = chart.chart.ctx as CanvasRenderingContext2D;
                ctx.save();
                ctx.fillStyle = "#FFFFFF";
                ctx.fillRect(0, 0, chart.chart.width, chart.chart.height);
                ctx.restore();
            },
        };

        const chartType =
            cd.type === "horizontalBar" ? "horizontalBar" : cd.type;

        this.chart = new Chart(canvas, {
            type: chartType as any,
            data,
            options,
            plugins: [whiteBackground],
        });
    }
}
